import { useEffect, useState } from "react";
import { Modal, Spin, Descriptions, Tag, Space } from "antd";
import apiHelpers from "./data";
import { triggerNotification } from "../../common/notify";
import { resolveBlogUrl } from "./stores";

const renderBodyHtml = (html) => {
  if (!html) return <em>No content.</em>;
  return (
    <iframe
      title="blog-body"
      sandbox=""
      srcDoc={html}
      style={{
        width: "100%",
        minHeight: 360,
        border: "1px solid #eee",
        borderRadius: 4,
        background: "#fff",
      }}
    />
  );
};

const BlogDetailModal = ({ open, blogId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    if (!open || !blogId) {
      setBlog(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchBlogById(blogId);
        if (!cancelled) setBlog(data);
      } catch (err) {
        const mapped = apiHelpers.mapError(err);
        triggerNotification({
          type: "error",
          title: "Failed to load blog",
          message: mapped.message,
        });
        if (!cancelled) onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, blogId, onClose]);

  const tags = blog?.tags || [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={820}
      destroyOnClose
      maskClosable={false}
      title={blog?.title || "Blog"}
    >
      {loading || !blog ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Author">{blog.author || "—"}</Descriptions.Item>
            <Descriptions.Item label="Store">{blog.storeLabel || "—"}</Descriptions.Item>
            <Descriptions.Item label="Published">
              {blog.isPublished ? (
                <Tag color="green">Published</Tag>
              ) : (
                <Tag color="gold">Draft</Tag>
              )}
            </Descriptions.Item>
            
            <Descriptions.Item label="BC Blog ID">{blog.bcBlogId || "—"}</Descriptions.Item>
            <Descriptions.Item label="URL" span={2}>
              {blog.blogUrl ? (
                (() => {
                  const fullUrl = resolveBlogUrl(blog.blogUrl, {
                    storeHash: blog.storeHash,
                    storeLabel: blog.storeLabel,
                  });
                  return (
                    <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                      {fullUrl}
                    </a>
                  );
                })()
              ) : (
                "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="BC Blog Tags" span={2}>
              {tags.length ? (
                <Space size={[4, 4]} wrap>
                  {tags.map((t) => (
                    <Tag key={t}>{t}</Tag>
                  ))}
                </Space>
              ) : (
                "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Meta description" span={2}>
              {blog.metaDescription || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Meta keywords" span={2}>
              {blog.metaKeywords || "—"}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Body</div>
          {renderBodyHtml(blog.body)}
        </>
      )}
    </Modal>
  );
};

export default BlogDetailModal;
